import { skipToken, useInfiniteQuery, useQueries, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import {
  CometUserRoleType,
  RentalManagerAccountViewZramMultifamilyWebApiHandler_list,
  RentalManagerAccountViewZramSingleFamilyWebApiHandler_list,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_listingQualityScore,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_options,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_prospects,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_sources,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_status,
  RentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising_queryOptions,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_prospects,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_sources,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_status,
  useRentalManagerUserPropertySpotlightWebApiHandler_overview,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { transformFiltersToApiParams } from '../../../utils/propertiesPageUtils';
import {
  chunkAdvertisingPages,
  fetchAdvertisingPage,
  findLatestPageReadOfRow,
  getAdvertisingPageKey,
  keepNewestRowRead,
  markRowRead,
  takeRowWriteSequence,
} from './zramAdvertisingPage';

import { useAuth } from '../../../components/AuthProvider';

import type { AccountViewListingsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../../utils/propertiesPageUtils';

// Re-export for convenience
export { transformFiltersToApiParams };

/**
 * Hook to fetch properties data for enterprise (ZRAM) customers.
 *
 * Automatically routes to single-family or multifamily API based on user type.
 * Supports infinite scroll pagination.
 */
export const useGetPropertiesData = (filters: FilterState, options?: { enabled?: boolean }) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching, isPlaceholderData } =
    useInfiniteQuery<
      AccountViewListingsDto,
      Error,
      { pages: Array<AccountViewListingsDto> },
      Array<string | FilterState>,
      string
    >({
      queryKey: ['getProperties', filters],
      enabled: options?.enabled ?? true,
      queryFn: ({ pageParam }) => {
        const apiParams = transformFiltersToApiParams(filters);
        if (isSingleFamilyEnterprise) {
          return RentalManagerAccountViewZramSingleFamilyWebApiHandler_list({
            ...apiParams,
            startKeyExclusive: pageParam,
          });
        }
        return RentalManagerAccountViewZramMultifamilyWebApiHandler_list({
          ...apiParams,
          startKeyExclusive: pageParam,
        });
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
      // @ts-expect-error - returning undefined as it would be during pending
      placeholderData: (previousData) => {
        if (!previousData?.pages?.[0]?.listingCount) {
          return;
        }

        return {
          pages: [
            {
              listingCount: previousData.pages[0].listingCount,
            },
          ],
          pageParams: previousData.pageParams,
        };
      },
      meta: {
        tags: ['getProperties'],
      },
    });

  const propertiesData = data?.pages.length
    ? {
        // The placeholder page carries listingCount only. Without this guard its missing listings array flattens
        // to a single undefined row, which throws as soon as a row destructures it.
        listings: data.pages.flatMap((page) => page.listings ?? []),
        listingAliasPages: data.pages.map((page) => (page.listings ?? []).map((listing) => listing.listingAlias)),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    isPending,
    isFetching,
    isPlaceholderData,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useZramStatus = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_status(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && !isSingleFamilyEnterprise : !isSingleFamilyEnterprise,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_status(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && isSingleFamilyEnterprise : isSingleFamilyEnterprise,
  });

  return isSingleFamilyEnterprise ? singleFamilyResult : multifamilyResult;
};

export const useZramListingQualityScore = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();

  // Skip listing quality score completely for single family enterprise users
  return useRentalManagerAccountViewZramMultifamilyWebApiHandler_listingQualityScore(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && !isSingleFamilyEnterprise : !isSingleFamilyEnterprise,
  });
};

export const useZramProspects = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_prospects(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && !isSingleFamilyEnterprise : !isSingleFamilyEnterprise,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_prospects(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && isSingleFamilyEnterprise : isSingleFamilyEnterprise,
  });

  return isSingleFamilyEnterprise ? singleFamilyResult : multifamilyResult;
};

export type AdvertisingPageState = 'pending' | 'ready' | 'fallback';

/**
 * Reads single family advertising a page at a time. A row falls back to its own request when its page failed or
 * came back without it.
 */
export const useZramAdvertisingPages = (listingAliasPages: string[][], options: { enabled: boolean }) => {
  const queryClient = useQueryClient();
  const pages = chunkAdvertisingPages(listingAliasPages);

  const pageResults = useQueries({
    queries: pages.map((pageListingAliases) => ({
      queryKey: getAdvertisingPageKey(pageListingAliases),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchAdvertisingPage(queryClient, pageListingAliases, { signal }),
      enabled: options.enabled,
      retry: false,
      meta: {
        tags: ['getProperties'],
        context: {
          api: 'RentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertisingPage',
          details: 'useZramAdvertisingPages',
        },
      },
    })),
  });

  const stateByListingAlias = new Map<string, AdvertisingPageState>();
  pages.forEach((pageListingAliases, index) => {
    const { data, isError } = pageResults[index];
    pageListingAliases.forEach((listingAlias) => {
      // A failed refetch keeps the page's last result, so its rows keep showing it instead of each reading alone.
      if ((isError && !data) || (data && !(listingAlias in data))) {
        stateByListingAlias.set(listingAlias, 'fallback');
      } else {
        stateByListingAlias.set(listingAlias, data ? 'ready' : 'pending');
      }
    });
  });
  return stateByListingAlias;
};

export const useZramAdvertising = (
  params: { listingAlias: string },
  options?: { enabled?: boolean; advertisingPageState?: AdvertisingPageState },
) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();
  const queryClient = useQueryClient();

  // Shares the 'getProperties' tag so spotlight mutations refresh every row's Advertising cell, not just theirs.
  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising(params, {
    enabled: options?.enabled !== undefined ? options.enabled && !isSingleFamilyEnterprise : !isSingleFamilyEnterprise,
    meta: { tags: ['getProperties'] },
  });

  // skipToken, not only enabled: false, because React Query still refetches a disabled query once nothing observes
  // it, which a row read by its page must never do.
  const isReadByPage = options?.advertisingPageState !== undefined && options.advertisingPageState !== 'fallback';
  const singleFamilyQueryOptions =
    RentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising_queryOptions(params);
  // A row reading on its own is ordered with the page reads, so it neither replaces a newer page result nor loses to
  // an older one.
  const readRowInOrder = async () => {
    const sequence = takeRowWriteSequence();
    return markRowRead(await singleFamilyQueryOptions.queryFn(), sequence);
  };
  const singleFamilyResult = useQuery({
    ...singleFamilyQueryOptions,
    queryFn: isReadByPage ? skipToken : readRowInOrder,
    structuralSharing: keepNewestRowRead(queryClient, singleFamilyQueryOptions.queryKey),
    initialData: () => findLatestPageReadOfRow(queryClient, params.listingAlias)?.advertising,
    initialDataUpdatedAt: () => findLatestPageReadOfRow(queryClient, params.listingAlias)?.updatedAt,
    enabled: (options?.enabled ?? true) && Boolean(isSingleFamilyEnterprise),
    meta: {
      tags: ['getProperties'],
      context: {
        api: 'RentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising',
        details: 'useZramAdvertising',
      },
    },
  });

  if (!isSingleFamilyEnterprise) {
    return multifamilyResult;
  }
  // The row's own query is idle while its page loads, so it reports loading here rather than an empty cell.
  const isWaitingForPage = options?.advertisingPageState === 'pending' && (options?.enabled ?? true);
  return isWaitingForPage ? { ...singleFamilyResult, isFetching: true } : singleFamilyResult;
};

export const useZramSources = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_sources(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && !isSingleFamilyEnterprise : !isSingleFamilyEnterprise,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_sources(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && isSingleFamilyEnterprise : isSingleFamilyEnterprise,
  });

  return isSingleFamilyEnterprise ? singleFamilyResult : multifamilyResult;
};

export const useZramOptions = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const isSingleFamilyEnterprise = useIsSingleFamilyEnterpriseExperience();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_options(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && !isSingleFamilyEnterprise : !isSingleFamilyEnterprise,
    meta: {
      tags: ['zramOptions', params.listingAlias],
      context: {
        api: 'RentalManagerAccountViewZramMultifamilyWebApiHandler_options',
        details: 'useZramOptions',
      },
    },
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options(params, {
    ...options,
    enabled: options?.enabled !== undefined ? options.enabled && isSingleFamilyEnterprise : isSingleFamilyEnterprise,
    meta: {
      tags: ['zramOptions', params.listingAlias],
      context: {
        api: 'RentalManagerAccountViewZramSingleFamilyWebApiHandler_options',
        details: 'useZramOptions',
      },
    },
  });

  return isSingleFamilyEnterprise ? singleFamilyResult : multifamilyResult;
};

export const useSpotlightOverview = () => {
  return useRentalManagerUserPropertySpotlightWebApiHandler_overview(
    {},
    {
      meta: {
        tags: ['spotlightOverview'],
        context: { api: 'spotlightOverview', details: 'spotlight overview totals' },
      },
    },
  );
};

// Multifamily customers always get the multifamily table, even when they also hold a single-family role.
export const useIsSingleFamilyEnterpriseExperience = () => {
  const { user } = useAuth();
  if (user?.isMultifamilyCustomer) {
    return false;
  }
  return (
    Boolean(user?.isSingleFamilyEnterpriseCustomer) ||
    Boolean(user?.roleTypes?.includes(CometUserRoleType.SPOTLIGHT_CUSTOMER))
  );
};
